import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { useAuthSessionSnapshot } from 'contexts/auth-store'
import { AuthService } from 'services/AuthService'
import { webLogger } from 'utils/logger'

import type { AppAuthUser } from 'services/AuthService'

type AuthContextValue = {
  user: AppAuthUser | undefined
  loading: boolean
  signInWithEmailAndPassword: (email: string, password: string) => Promise<void>
  signUpWithEmailAndPassword: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithGoogleIdToken: (token: string, nonce?: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const signInWithEmailAndPassword = async (email: string, password: string) => {
  try {
    await AuthService.signInWithEmailAndPassword(email, password)
  } catch (error) {
    webLogger.error('Error signing in with email and password', { error })
    throw error
  }
}

const signUpWithEmailAndPassword = async (email: string, password: string) => {
  try {
    await AuthService.signUpWithEmailAndPassword(email, password)
  } catch (error) {
    webLogger.error('Error signing up with email and password', { error })
    throw error
  }
}

const signInWithGoogle = async () => {
  try {
    await AuthService.signInWithGoogle()
  } catch (error) {
    webLogger.error('Error signing in with Google', { error })
    throw error
  }
}

const signInWithGoogleIdToken = async (token: string, nonce?: string) => {
  try {
    await AuthService.signInWithGoogleIdToken(token, nonce)
  } catch (error) {
    webLogger.error('Error signing in with Google ID token', { error })
    throw error
  }
}

const signOut = async () => {
  try {
    await AuthService.signOut()
  } catch (error) {
    webLogger.error('Error signing out', { error })
    throw error
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  /* --- Section 1: Hooks --- */
  const { loading, user } = useAuthSessionSnapshot()

  /* --- Section 3: Derived Values --- */
  const value = useMemo(
    () => ({
      user,
      loading,
      signInWithEmailAndPassword,
      signUpWithEmailAndPassword,
      signInWithGoogle,
      signInWithGoogleIdToken,
      signOut,
    }),
    [loading, user]
  )

  /* --- Section 6: Render --- */
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
